import { retrieveCatalogSearchResults } from "@/api/orval/catalog-search/catalog-search";
import type {
  CatalogSearchQueryRequest,
  CatalogSearchSortOrder,
  DisplayCatalogSearchResult,
} from "@/api/orval/model";

/**
 * A search client with the shape of Algolia's `LiteClient` that answers from the in-house catalog search
 * (`POST /catalog-search`, Postgres) instead of Algolia. InstantSearch and the catalog hooks keep sending the
 * requests they always did — index name, query, filter string, facet filters, facets, page — and this turns
 * them into the endpoint's request and its results back into Algolia's response shape.
 */

/** The index every request names; anything after it is the sort replica. */
const SEARCH_INDEX_SUFFIX = "_search";

/** Algolia's own default when a request names no page size. */
const ALGOLIA_DEFAULT_HITS_PER_PAGE = 20;

const SORT_BY_REPLICA: Readonly<Record<string, CatalogSearchSortOrder>> = {
  "": "Default",
  "_on_sale": "OnSale",
  "_best_selling": "BestSelling",
  "_price_asc": "PriceAsc",
  "_price_desc": "PriceDesc",
  "_recent": "Recent",
};

/**
 * Parameters that only shaped Algolia's analytics or highlighting. The in-house search keeps no analytics and
 * the app renders no highlights, so they change nothing about the answer.
 */
const PRESENTATION_ONLY_PARAMETERS = new Set([
  "analytics",
  "analyticsTags",
  "attributesToHighlight",
  "clickAnalytics",
  "highlightPreTag",
  "highlightPostTag",
  "maxValuesPerFacet",
]);

const ANSWERED_PARAMETERS = new Set([
  "query",
  "filters",
  "facetFilters",
  "facets",
  "page",
  "hitsPerPage",
  "attributesToRetrieve",
]);

type FacetFilters = string | readonly (string | readonly string[])[];

type SearchParameters = {
  query?: string;
  filters?: string;
  facetFilters?: FacetFilters;
  facets?: string | readonly string[];
  page?: number;
  hitsPerPage?: number;
  attributesToRetrieve?: readonly string[];
  tagFilters?: unknown;
} & Record<string, unknown>;

type SearchRequest = { indexName: string } & SearchParameters;

type SearchArgument =
  | readonly { indexName: string; params?: SearchParameters }[]
  | { requests: readonly SearchRequest[] };

const toSearchRequests = (argument: SearchArgument): SearchRequest[] =>
  Array.isArray(argument)
    ? argument.map(({ indexName, params }) => ({ indexName, ...params }))
    : (argument as Extract<SearchArgument, { requests: unknown }>).requests.map((request) => ({
        ...request,
      }));

const sortOf = (indexName: string): CatalogSearchSortOrder => {
  const at = indexName.lastIndexOf(SEARCH_INDEX_SUFFIX);
  const sort =
    at < 0 ? undefined : SORT_BY_REPLICA[indexName.slice(at + SEARCH_INDEX_SUFFIX.length)];
  if (!sort) throw new Error(`Catalog search has no index named ${indexName}`);
  return sort;
};

const isEmptyTagFilters = (tagFilters: unknown) =>
  tagFilters === undefined ||
  tagFilters === "" ||
  (Array.isArray(tagFilters) && tagFilters.length === 0);

const assertAnswerable = ({ indexName: _indexName, tagFilters, ...parameters }: SearchRequest) => {
  if (!isEmptyTagFilters(tagFilters)) {
    throw new Error("Catalog search cannot answer tagFilters");
  }
  for (const [name, value] of Object.entries(parameters)) {
    if (
      value === undefined ||
      ANSWERED_PARAMETERS.has(name) ||
      PRESENTATION_ONLY_PARAMETERS.has(name)
    )
      continue;
    throw new Error(`Catalog search cannot answer the '${name}' search parameter`);
  }
};

const toFacetFilterGroups = (facetFilters: FacetFilters | undefined): string[][] => {
  if (facetFilters === undefined) return [];
  if (typeof facetFilters === "string") return [[facetFilters]];
  return facetFilters.map((group) => (typeof group === "string" ? [group] : [...group]));
};

export const toCatalogSearchQueryRequest = (request: SearchRequest): CatalogSearchQueryRequest => {
  assertAnswerable(request);
  return {
    query: request.query ?? "",
    filters: request.filters ?? "",
    facetFilters: toFacetFilterGroups(request.facetFilters),
    facets: typeof request.facets === "string" ? [request.facets] : [...(request.facets ?? [])],
    sort: sortOf(request.indexName),
    page: request.page ?? 0,
    hitsPerPage: request.hitsPerPage ?? ALGOLIA_DEFAULT_HITS_PER_PAGE,
    attributesToRetrieve: request.attributesToRetrieve ? [...request.attributesToRetrieve] : null,
  };
};

const toAlgoliaResult = (result: DisplayCatalogSearchResult, indexName: string) => ({
  hits: result.hits,
  nbHits: result.nbHits,
  page: result.page,
  nbPages: result.nbPages,
  hitsPerPage: result.hitsPerPage,
  facets: result.facets,
  exhaustiveNbHits: true,
  processingTimeMS: 0,
  query: result.query,
  params: "",
  index: indexName,
});

const answerFromCatalogSearch = async (argument: SearchArgument) => {
  const requests = toSearchRequests(argument);
  const { results } = await retrieveCatalogSearchResults({
    requests: requests.map(toCatalogSearchQueryRequest),
  });
  if (results.length !== requests.length) {
    throw new Error(`Catalog search answered ${results.length} of ${requests.length} searches`);
  }
  return {
    results: results.map((result, index) => toAlgoliaResult(result, requests[index].indexName)),
  };
};

export const catalogSearchClient = () => ({
  appId: "",
  transporter: undefined,
  search: (argument: SearchArgument) => answerFromCatalogSearch(argument),
});

/**
 * Routes every search to the in-house catalog search while `isOn()` resolves true, and to `client` otherwise.
 * Read per search so the flag can be turned on — or back off — for a practice without a deploy or a reload.
 */
export const withCatalogSearchWhen = <
  Client extends { search: (...args: never[]) => Promise<unknown> },
>(
  client: Client,
  isOn: () => Promise<boolean>,
): Client => {
  const inHouse = catalogSearchClient();
  const search = async (argument: SearchArgument, ...rest: unknown[]) =>
    (await isOn())
      ? inHouse.search(argument)
      : (client.search as (...args: unknown[]) => Promise<unknown>)(argument, ...rest);
  return { ...client, search } as Client;
};
